
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { useInView } from '../utils/animations';
import { z } from 'zod';
import { formSchema, sendContactEmail, prepareMailtoFallback, SERVICE_ID, TEMPLATE_ID, PUBLIC_KEY } from '@/utils/contactFormUtils';
import { useIsMobile } from '@/hooks/use-mobile';
import PersonalDetailsFields from './contact/PersonalDetailsFields';
import WeddingDetailsFields from './contact/WeddingDetailsFields';
import SubmitButton from './contact/SubmitButton';

const ContactForm = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [formRef, formInView] = useInView({ threshold: 0.1 });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);
  const isMobile = useIsMobile();
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      wedding_location: '',
      ceremony_type: 'koscielny',
      service_type: 'fotografia',
      message: '',
    },
  });
  
  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);
    setErrorMessage(null);
    setDebugInfo(null);
    setIsError(false);
    
    try {
      console.log("Inicjalizuję EmailJS z kluczem:", PUBLIC_KEY);
      
      try {
        console.log('Używane ID serwisu EmailJS:', SERVICE_ID);
        console.log('Używane ID szablonu EmailJS:', TEMPLATE_ID);
        
        const response = await sendContactEmail(data);
        
        console.log('Email wysłany pomyślnie, odpowiedź:', response);
        
        setIsSuccess(true);
        setIsError(false);
        form.reset();
        
        toast({
          title: "Wiadomość wysłana",
          description: "Dziękujemy za kontakt. Odpowiemy najszybciej jak to możliwe.",
        });
        
        setTimeout(() => {
          setIsSuccess(false);
        }, 5000);
      } catch (sendError) {
        console.error('Błąd podczas wysyłania przez EmailJS:', sendError);
        
        let errorDetails = "Nieznany błąd";
        if (sendError instanceof Error) {
          errorDetails = sendError.message;
          setDebugInfo(`Błąd EmailJS: ${sendError.message}`);
        } else {
          setDebugInfo(`Błąd EmailJS: ${JSON.stringify(sendError)}`);
        }
        
        setIsError(true);
        throw new Error(`Nie udało się wysłać wiadomości: ${errorDetails}`);
      }
    } catch (error) {
      console.error('Błąd wysyłania wiadomości:', error);
      
      setErrorMessage(
        error instanceof Error 
          ? error.message 
          : "Wystąpił nieznany błąd podczas wysyłania wiadomości."
      );
      
      setIsError(true);
      
      toast({
        title: "Błąd",
        description: "Nie udało się wysłać wiadomości. Spróbuj ponownie później lub skontaktuj się z nami bezpośrednio.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMailtoFallback = () => {
    const data = form.getValues();
    const { subject, body } = prepareMailtoFallback(data);
    
    window.location.href = `mailto:kontakt@czuleklisze.pl?subject=${subject}&body=${body}`;
    
    toast({
      title: "Alternatywna metoda",
      description: "Otworzyliśmy klienta poczty z przygotowaną wiadomością.",
    });
  };

  return (
    <section id="contact-form" className="py-8 md:py-16 bg-white">
      <div 
        ref={formRef as any} 
        className={`container-custom ${isMobile ? 'px-3' : 'max-w-5xl'} mx-auto ${
          formInView ? 'opacity-100 translate-y-0 transition-all duration-700' : 'opacity-0 translate-y-10 transition-all duration-700'
        }`}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className={`grid grid-cols-1 ${isMobile ? 'gap-4' : 'md:grid-cols-2 gap-6'}`}>
              <PersonalDetailsFields form={form} />
              <WeddingDetailsFields form={form} />
            </div>
            
            <SubmitButton 
              isSubmitting={isSubmitting}
              isSuccess={isSuccess}
              isError={isError}
            />
          </form>
        </Form>
      </div>
    </section>
  );
};

export default ContactForm;
